<template>
    <div class="notifications-container">
        <button class="toggle-btn" @click="toggleNotifications">
            <span class="badge">{{ unreadNotifiations }}</span>
        </button>
        <div class="notifications" v-if="showNotifications">
            <div v-for="notification in notifications" :key="notification._id"
                class="notification" :class="{read: notification.read}"
                @click="markAsRead(notification)"
                >
                <div>
                    {{notification.message}}
                </div>
                <button class="btn-delete" @click="deleteNotification(notification)">X</button>
            </div>
            <div v-if="notifications.length == 0" class="notifications-empty">
                {{$t("admin.no_notifications")}}
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import {defineComponent, inject} from "vue"
import type SocketClient from "xpark-shared/services/socket-client"
import {Topic} from "xpark-shared/topics"
import UtilsMixin from "@/mixins/UtilsMixin"
import {GAME_UPDATE_ROOM} from "xpark-shared/variables"

class Notification {
    message: string = ""
    read: boolean = false
    _id: string = ""
    insert_at: number = 0

    constructor(data: any) {
        this.message = data.message
        this.read = data.read
        this._id = data._id
        this.insert_at = data.insert_at
    }
}

const STORAGE_KEY = "xpark_notifications"

export default defineComponent({
    setup() {
        return {
            socketClient: inject("socketClient") as SocketClient
        }
    },

    mixins: [UtilsMixin],

    data() {
        return {
            notifications: [] as Notification[],
            showNotifications: false,
            clickHandler: null as any,
            listener: (data: any) => this.onGameStatus(data)
        }
    },

    mounted() {
        this.loadLocalNotifications()
        this.socketClient?.joinRoom(GAME_UPDATE_ROOM)
        this.socketClient?.on(Topic.gameStatus, this.listener)

        this.clickHandler = (e: any) => this.clickDocument(e)
        document.addEventListener("click", this.clickHandler)
    },

    unmounted() {
        this.socketClient?.off(Topic.gameStatus, this.clickHandler)
        document.removeEventListener("click", this.clickHandler)
    },

    computed: {
        unreadNotifiations() {
            return this.notifications.filter(n => !n.read).length
        }
    },

    methods: {
        onGameStatus(data: any) {
            if (data.notifications === undefined) return

            const receivedNotifications = data.notifications.sort((a: any, b: any) => b.insert_at - a.insert_at)
            const newNotifications = receivedNotifications.filter((n: any) => !this.notifications.map(x => x._id).includes(n._id))
            if (newNotifications.length > 0) {
                for (const newNot of newNotifications) {
                    this.notifications.unshift(new Notification(newNot))
                }
                this.saveNotifications()
                this.showNotifications = true
            }
        },

        loadLocalNotifications() {
            const json = localStorage.getItem(STORAGE_KEY)
            if (json) {
                const notifications = JSON.parse(json)
                this.notifications = notifications.map((n: any) => new Notification(n))
            }
        },

        onAdminWarning() {
            this.showNotifications = true

            this.saveNotifications()
        },

        toggleNotifications() {
            this.showNotifications = !this.showNotifications
        },

        deleteNotification(notification: Notification) {
            this.notifications = this.notifications.filter(n => n._id !== notification._id)
            this.socketClient.emit(Topic.deleteNotification, {id:notification._id})
            this.saveNotifications()
        },

        markAsRead(notification: Notification) {
            notification.read = true

            this.saveNotifications()
        },

        saveNotifications() {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(this.notifications))
        },

        clickDocument(e: any) {
            if (e.target.closest(".notifications-container") || e.target.classList.contains("btn-delete")) {
                return
            }
            this.showNotifications = false
        }
    }
})
</script>

<style scoped lang="scss">
.notifications-container {
    position: relative;
    margin-right: 0.5rem;

    .notifications {
        position: absolute;
        background: white;
        width: 50rem;
        top: 4.5rem;
        border: solid .1rem $color-blue;
        box-shadow: 0.2rem 0.2rem 0.25rem $color-blue;

        .notification {
            padding:  0.5rem 1rem;
            border-bottom: solid .1rem $color-blue;
            font-weight: bold;
            display: flex;
            justify-content: space-between;
            align-items: center;

            &.read {
                font-weight: normal;
            }
        }
    }

    .notifications-empty {
        padding:  0.5rem 1rem;
    }

    .btn-delete {
        background-color: transparent;
        border: solid .1rem $color-red;
        height: 3rem;
        width: 3rem;
        color: $color-red;
        font-weight: bold;
        font-size: 1.5rem;
        cursor: pointer;
    }
}

.toggle-btn {
    background-color: transparent;
    border: solid 0.25rem white;
    background-image : url("/img/notification.svg");
    width: 4rem;
    height: 4rem;
    margin-right: 1rem;
    background-size: 50%;
    background-repeat: no-repeat;
    background-position: center;
    border-radius: 50%;
    position: relative;

    .badge {
        position: absolute;
        top: -1rem;
        right: -1.25rem;
        width: 2.5rem;
        height: 2.5rem;
        background-color: $color-red;
        color: white;
        font-size: 1.5rem;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
    }
}
</style>
